/**
 * useAircraftMovementLog - Fetch allocation events for an aircraft
 *
 * Returns the last N events from allocation_events table.
 * (Phase A: migrated from allocation_history)
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface MovementLogEntry {
  id: string
  /** Event type: placed, arrived, departed, departed_final, moved, unit_changed */
  eventType: string
  /** Source: manual, flightaware, adsb, system, schedule */
  eventSource: string
  eventAt: string
  areaName: string | null
  unitCode: string | null
  notes: string | null
}

export function useAircraftMovementLog(aircraftId: string | null) {
  return useQuery({
    queryKey: ['aircraft-movement-log', aircraftId],
    queryFn: async () => {
      if (!aircraftId) return []

      const { data, error } = await supabase
        .from('allocation_events')
        .select(`
          id,
          event_type,
          event_source,
          event_at,
          notes,
          areas:area_id (name),
          units:unit_id (unit_code)
        `)
        .eq('aircraft_id', aircraftId)
        .order('event_at', { ascending: false })
        .limit(10)

      if (error) throw error
      if (!data) return []

      return data.map((row: any): MovementLogEntry => ({
        id: row.id,
        eventType: row.event_type,
        eventSource: row.event_source,
        eventAt: row.event_at,
        areaName: row.areas?.name ?? null,
        unitCode: row.units?.unit_code ?? null,
        notes: row.notes,
      }))
    },
    enabled: !!aircraftId,
    staleTime: 60_000,
  })
}
